import { API, APIResponse, APIPaginatedResponse } from '../types';
import { action } from 'mobx';
import {
  ChannelSchema, CardSchema,
  OrderSchema, CouponSchema, UserPreferencesSchema, InviteSchema,
  LicenceSchema
} from '../../../types/schema';
import {User} from '../../../types/user';
import {AccountsAPI, CouponAPI} from '../../../types/api';
import {YouTubeVideo} from '../../../types/youtube';
import {BillingAddress} from '../../../types';
import encodeQueryString from '../../../lib/encodeQueryString';
import {Coupon} from '../../../components/user/coupon';
import {stringify} from "querystring";

export class UserAPIController extends API {
  @action
  getToken = async (code: string, identifiers: {[key: string]: any}) => {
    const url = `/user/youtube/create?code=${code}&${encodeQueryString(identifiers)}`;
    const resp = await this.request.get({url});

    return await resp.json() as {token: string};
  };

  @action
  setDefaultChannel = async (default_channel: string) => {
    const url = `/user/session`;
    const body = JSON.stringify({ default_channel });
    const resp = await this.request.patch({ url, body });

    return await resp.json() as APIResponse<User>;
  };

  @action
  setVatNumber = async (vat_number: string) => {
    const url = `/user/session`;
    const body = JSON.stringify({ vat_number });
    const resp = await this.request.patch({ url, body });

    return await resp.json() as APIResponse<User>;
  };

  @action
  setContactDetails = async (values: { email: string, telephone?: string, country: string }) => {
    const url = `/user/session`;
    const body = JSON.stringify(values);
    const resp = await this.request.patch({ url, body });

    return await resp.json() as APIResponse<User>;
  };

  @action
  setMarketingPreferences = async (marketing_allowed: boolean) => {
    const url = `/user/session`;
    const body = JSON.stringify({ marketing_allowed });
    const resp = await this.request.patch({ url, body });

    return await resp.json() as APIResponse<User>;
  };

  @action
  setOnboardingState = async (onboarding_state: string) => {
    const url = `/user/session`;
    const body = JSON.stringify({ onboarding_state });
    const resp = await this.request.patch({ url, body });

    return await resp.json() as APIResponse<User>;
  };

  @action
  getAccounts = async (userIdentity: string) => {
    const url = `/user/${userIdentity}/accounts`;
    const resp = await this.request.get({ url });
    return await resp.json() as APIResponse<AccountsAPI>;
  };

  @action
  getPreferences = async (userIdentity: string) => {
    const url = `/user/${userIdentity}/preferences`;
    const resp = await this.request.get({ url });
    return await resp.json() as APIResponse<UserPreferencesSchema>;
  };

  @action
  setPreferences = async (userIdentity: string, preferences: Partial<UserPreferencesSchema>) => {
    const url = `/user/${userIdentity}/preferences`;
    const body = JSON.stringify(preferences);
    return await this.request.patch({ url, body });
  };

  @action
  deleteAccount = async () => {
    const url = `/user/session`;
    const body = JSON.stringify({ delete_account: true });
    const resp = await this.request.patch({ url, body });

    return await resp.json() as APIResponse<User>;
  };

  @action
  downloadAccountData = async () => {
    const url = `/user/session`;
    const body = JSON.stringify({ download_request: true });
    const resp = await this.request.patch({ url, body });

    return await resp.json() as APIResponse<User>;
  };

  @action
  updateSurvey = async (userIdentity: string, survey: string, responses: {[question: string]: string}) => {
    const url = `/user/${userIdentity}/surveys`;
    const body = JSON.stringify({ survey, responses });

    return await this.request.post({ url, body });
  };

  @action
  clearPreferences = async (userIdentity: string) => {
    const url = `/user/${userIdentity}/preferences`;
    const body = JSON.stringify({
      daily_newmusic_email: false,
      daily_recommendation_email: false,
      monthly_newmusic_email: false,
      monthly_recommendation_email: false,
      product_announcement_email: false,
      thirdparty_promotions_email: false,
      twitter_order_announcement: false,
      weekly_newmusic_email: false,
      weekly_recommendation_email: false,
    });

    return await this.request.patch({ url, body });
  };

  @action
  getYouTubeChannels = async (userIdentity: string) => {
    const url = `/user/${userIdentity}/youtube/channels`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<ChannelSchema>>;

    return json;
  };

  @action
  getYouTubeChannel = async (userIdentity: string, identity: string, brandSponsored: boolean = false) => {
    const url = `/user/${userIdentity}/youtube/channels/${identity}${brandSponsored ? `?brand_sponsored=1` : ''}`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<ChannelSchema>;

    return json;
  };

  @action
  getYouTubeChannelVideos = async (userIdentity: string, identity: string, page: number = 1) => {
    const url = `/user/${userIdentity}/youtube/channels/${identity}/videos?page=${page}`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIPaginatedResponse<Array<YouTubeVideo>>;

    return json;
  };

  @action
  createBillingAddress = async (userIdentity: string, address: BillingAddress) => {
    const url = `/user/${userIdentity}/addresses`;
    // const body = JSON.stringify(address);
    const body = JSON.stringify({
      ...address,
      country_id: address.country
    });

    const resp = await this.request.post({ url, body });

    return await resp.json();
  };

  @action
  createCoupon = async (userIdentity: string, channelId: string) => {
    const url = `/user/${userIdentity}/coupon`;
    const body = JSON.stringify({ channelId });
    const resp = await this.request.post({ url, body });

    return (await resp.json()) as APIResponse<CouponSchema>;
  };

  @action
  getBillingAddresses = async (userIdentity: string) => {
    const url = `/user/${userIdentity}/addresses`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<BillingAddress>>;

    return json;
  };

  @action
  getPaymentCards = async (userIdentity: string) => {
    const url = `/user/${userIdentity}/payments`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<CardSchema>>;

    return json;
  };

  @action
  getLicencedTracks = async (userIdentity: string, page: number = 1, perPage: number = 20) => {
    const url = `/user/${userIdentity}/licences?include=order,track.artists,track.images&page=${page}&perPage=${perPage}`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIPaginatedResponse<Array<LicenceSchema>>;

    return json;
  };

  @action
  getLicence = async (userIdentity: string, identity: string) => {
    const url = `/user/${userIdentity}/licences/${identity}?include=order,track.artists`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<LicenceSchema>;

    return json;
  };

  @action
  getOrders = async (userIdentity: string, page: number = 1) => {
    const url = `/user/${userIdentity}/orders?page=${page}`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIPaginatedResponse<Array<OrderSchema>>;

    return json;
  };

  @action
  getOrder = async (userIdentity: string, identity: string) => {
    const url = `/user/${userIdentity}/orders/${identity}`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<OrderSchema>;

    return json;
  };

  @action
  unlinkSpotify = async (userIdentity: string) => this.unlink(userIdentity, 'spotify');

  @action
  unlinkTwitter = async (userIdentity: string) => this.unlink(userIdentity, 'twitter');

  @action
  unlinkDropbox = async (userIdentity: string) => this.unlink(userIdentity, 'dropbox');

  @action
  unlinkFacebook = async (userIdentity: string) => this.unlink(userIdentity, 'facebook');

  @action
  private unlink = async (userIdentity: string, service: string) => {
    const url = `/user/${userIdentity}/${service}`;
    const resp = await this.request.del({ url });

    return resp.json();
  };

  @action
  getInvites = async (userIdentity: string) => {
    const url = `/user/${userIdentity}/invite`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIResponse<Array<InviteSchema>>;

    return json;
  };

  @action
  createInvite = async (userIdentity: string, name: string, email: string) => {
    const url = `/user/${userIdentity}/invite`;
    const body = JSON.stringify({ name, email });
    const resp = await this.request.post({ url, body });

    return await resp.json();
  };

  @action
  addChannel = async (userIdentity: string, channel: ChannelSchema) => {
    const url = `/user/${userIdentity}/youtube/channels`;
    const body = JSON.stringify(channel);
    const resp = await this.request.post({ url, body });

    return await resp.json();
  };

  @action
  getCoupons = async (userIdentity: string): Promise<APIPaginatedResponse<Coupon[]>> => {
    const url = `/user/${userIdentity}/coupons`;
    const resp = await this.request.get({ url });
    const json = await resp.json() as APIPaginatedResponse<Coupon[]>;

    return json;
  }
}